import { Milliseconds, DataLifeCyclePeriod } from '../../../src/constants'
import { HistoryStore } from '../constructs/HistoryStore'
import {
  HistoryObjectType,
  HistoryStoreSystemAtts,
  isHistoryObject,
  MapLike,
  type ObjectHistoryFilterRequest,
  PossibleBusinessObject,
  ValidBusinessObject,
} from '@iotv/iotv-v3-types'
import { ModelCommon } from '../ModelCommon'
import { v4 } from 'uuid'
import { enumerable } from '../../model/constructs'

export type ObjectHistoryProps<T extends { [k: string]: HistoryStore } = {}> = PossibleBusinessObject & {
  recentStateHistory?: HistoryStore
  dailyStateHistory?: HistoryStore
  weeklyStateHistory?: HistoryStore
  monthlyStateHistory?: HistoryStore
  yearlyStateHistory?: HistoryStore
} & T

export type CustomObjectHistory<T extends { [k: string]: any }> = ObjectHistory & {
  [k in keyof T]: HistoryStore
}

export type ObjectHistoryConstrutionProps = PossibleBusinessObject & {
  recentStateHistory?: HistoryObjectType
  dailyStateHistory?: HistoryObjectType
  weeklyStateHistory?: HistoryObjectType
  monthlyStateHistory?: HistoryObjectType
  yearlyStateHistory?: HistoryObjectType
}

export type ObjectHistoryConfig = {
  recentStateHistory?: boolean
  dailyStateHistory?: boolean
  weeklyStateHistory?: boolean
  monthlyStateHistory?: boolean
  yearlyStateHistory?: boolean
}

export class ObjectHistory extends ModelCommon implements ObjectHistoryProps {
  static typeName = 'ObjectHistory'

  static defaultReceentHistoryObjectSystemProperties: HistoryStoreSystemAtts = {
    autoTrim: true,
    maxAgeMS: Milliseconds.DAY * 365,
    maxLength: undefined,
  }

  static defaultDailyHistoryObjectSystemProperties: HistoryStoreSystemAtts = {
    autoTrim: true,
    maxAgeMS: Milliseconds.DAY * 365 * 5,
    maxLength: undefined,
  }

  static defaultWeeklyHistoryObjectSystemProperties: HistoryStoreSystemAtts = {
    autoTrim: true,
    maxAgeMS: Milliseconds.DAY * 365 * 10,
    maxLength: undefined,
  }

  static defaultMonthlyHistoryObjectSystemProperties: HistoryStoreSystemAtts = {
    autoTrim: true,
    maxAgeMS: Milliseconds.DAY * 365 * 20,
    maxLength: undefined,
  }

  static defaultYearlyHistoryObjectSystemProperties: HistoryStoreSystemAtts = {
    autoTrim: true,
    maxAgeMS: Milliseconds.DAY * 365 * 50,
    maxLength: undefined,
  }

  static getDefaultHistoryObjectParams = (period: DataLifeCyclePeriod): HistoryObjectType => {
    let systemProperties: HistoryStoreSystemAtts
    switch (period) {
      case DataLifeCyclePeriod.DEVICE_PERIOD:
        systemProperties = ObjectHistory.defaultReceentHistoryObjectSystemProperties
        break
      case DataLifeCyclePeriod.DAILY:
        systemProperties = ObjectHistory.defaultDailyHistoryObjectSystemProperties
        break
      case DataLifeCyclePeriod.WEEKLY:
        systemProperties = ObjectHistory.defaultWeeklyHistoryObjectSystemProperties
        break
      case DataLifeCyclePeriod.MONTHLY:
        systemProperties = ObjectHistory.defaultMonthlyHistoryObjectSystemProperties
        break
      case DataLifeCyclePeriod.YEARLY:
        systemProperties = ObjectHistory.defaultYearlyHistoryObjectSystemProperties
        break
      default:
        systemProperties = ObjectHistory.defaultDailyHistoryObjectSystemProperties
    }
    return {
      index: [],
      timestamp: [],
      systemProperties,
    }
  }

  static defaultSettings: MapLike<HistoryObjectType> = {
    recentStateHistory: ObjectHistory.getDefaultHistoryObjectParams(DataLifeCyclePeriod.DEVICE_PERIOD),
    dailyStateHistory: ObjectHistory.getDefaultHistoryObjectParams(DataLifeCyclePeriod.DAILY),
    weeklyStateHistory: ObjectHistory.getDefaultHistoryObjectParams(DataLifeCyclePeriod.WEEKLY),
    monthlyStateHistory: ObjectHistory.getDefaultHistoryObjectParams(DataLifeCyclePeriod.MONTHLY),
    yearlyStateHistory: ObjectHistory.getDefaultHistoryObjectParams(DataLifeCyclePeriod.YEARLY),
    default: { timestamp: [], index: [] },
  }

  static createForObject(vob: ValidBusinessObject, config: ObjectHistoryConfig): ObjectHistory {
    const id = v4()
    const sk = `${ObjectHistory.typeName}_${vob.sk}`
    let params: ObjectHistoryProps = {
      type: ObjectHistory.typeName,
      id,
      pk: vob.sk,
      sk,
      name: `${vob.name ?? vob.sk}_History`,
    }
    if (config.recentStateHistory) params.recentStateHistory = new HistoryStore(this.getDefaultHistoryObjectParams(DataLifeCyclePeriod.DEVICE_PERIOD))
    if (config.dailyStateHistory) params.dailyStateHistory = new HistoryStore(this.getDefaultHistoryObjectParams(DataLifeCyclePeriod.DAILY))
    if (config.weeklyStateHistory) params.weeklyStateHistory = new HistoryStore(this.getDefaultHistoryObjectParams(DataLifeCyclePeriod.WEEKLY))
    if (config.monthlyStateHistory) params.monthlyStateHistory = new HistoryStore(this.getDefaultHistoryObjectParams(DataLifeCyclePeriod.MONTHLY))
    if (config.yearlyStateHistory) params.yearlyStateHistory = new HistoryStore(this.getDefaultHistoryObjectParams(DataLifeCyclePeriod.YEARLY))

    return new ObjectHistory(params)
  }

  dailyStateHistory?: HistoryStore
  recentStateHistory?: HistoryStore
  weeklyStateHistory?: HistoryStore
  monthlyStateHistory?: HistoryStore
  yearlyStateHistory?: HistoryStore
  type: string = ObjectHistory.typeName

  oldconstructor({ recentStateHistory, dailyStateHistory, weeklyStateHistory, monthlyStateHistory, yearlyStateHistory, ...ob }: ObjectHistoryConstrutionProps) {
    // super(ob.type ?? ObjectHistory.typeName, ob)
    if (recentStateHistory) {
      this.recentStateHistory = new HistoryStore({ ...ObjectHistory.getDefaultHistoryObjectParams(DataLifeCyclePeriod.DEVICE_PERIOD), ...recentStateHistory })
    }
    if (dailyStateHistory) {
      this.dailyStateHistory = new HistoryStore({ ...ObjectHistory.getDefaultHistoryObjectParams(DataLifeCyclePeriod.DAILY), ...dailyStateHistory })
    }
    if (weeklyStateHistory) {
      this.weeklyStateHistory = new HistoryStore({ ...ObjectHistory.getDefaultHistoryObjectParams(DataLifeCyclePeriod.WEEKLY), ...weeklyStateHistory })
    }
    if (monthlyStateHistory) {
      this.monthlyStateHistory = new HistoryStore({ ...ObjectHistory.getDefaultHistoryObjectParams(DataLifeCyclePeriod.MONTHLY), ...monthlyStateHistory })
    }
    if (yearlyStateHistory) {
      this.yearlyStateHistory = new HistoryStore({ ...ObjectHistory.getDefaultHistoryObjectParams(DataLifeCyclePeriod.YEARLY), ...yearlyStateHistory })
    }
    if (ob.type) {
      this.type = ob.type
    }
  }

  constructor({ ...ob }: ObjectHistoryConstrutionProps) {
    super(ob.type ?? ObjectHistory.typeName, ob)
    Object.entries(ob)
      .filter(([k, v]) => v && isHistoryObject(v))
      .forEach(([k, v]) => {
        const systemProperties = ObjectHistory.defaultSettings[k]
        this[k] = new HistoryStore(v)
      })
    if (ob.type) {
      this.type = ob.type
    }
  }

  @enumerable(false)
  keys() {
    return Object.entries(this)
      .filter(([k, v]) => v instanceof HistoryStore)
      .map(([k, v]) => k)
  }

  @enumerable(false)
  clone(keys: string[] = this.keys()) {
    if (keys.length === 0) {
      keys = this.keys()
    }
    const objectHistoryProps: ObjectHistoryProps = {
      type: this.type,
      sk: this.sk,
      pk: this.pk,
      name: this.name,
      id: this.id,
    }
    if (this.metadata) {
      objectHistoryProps.metadata = this.metadata
    }
    keys.forEach((k) => (objectHistoryProps[k] = this[k] instanceof HistoryStore ? this[k].clone() : undefined))
    return new ObjectHistory(objectHistoryProps)
  }

  @enumerable(false)
  filter(objectHistoryFilter: ObjectHistoryFilterRequest) {
    const filterKeys = Object.keys(objectHistoryFilter)
    const clone = this.clone(filterKeys)
    filterKeys.forEach((k) => {
      if (clone[k] instanceof HistoryStore) {
        clone[k] = clone[k].filter(objectHistoryFilter[k])
      }
    })
    return clone
  }
}
